import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useI18n, fill } from "@/i18n";
import Seo from "@/components/Seo";
import TransferCta from "@/components/TransferCta";
import { attractionBySlug, ATTRACTIONS } from "@/data/attractions";
import { orgSchema, breadcrumb, faqSchema } from "@/lib/seo";
import { ArrowLeft, CheckCircle, MapPin } from "@phosphor-icons/react";

export default function AttractionDetail() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const a = attractionBySlug(slug);
  if (!a) { navigate("/ausfluege"); return null; }
  const c = a[lang] || a.de;

  const title = lang === "de"
    ? `Flughafentransfer ${c.name} ab Zürich (ZRH) | Festpreis`
    : `Airport Transfer to ${c.name} from Zürich (ZRH) | Fixed Fare`;
  const desc = `${c.intro} ${c.why}`;
  const faqs = [
    { q: fill(t("att_faq1_q"), { name: c.name }), a: fill(t("att_faq1_a"), { name: c.name }) },
    { q: fill(t("att_faq2_q"), { name: c.name }), a: t("att_faq2_a") },
    { q: t("att_faq3_q"), a: t("att_faq3_a") },
  ];

  const benefits = [t("dben_fixed"), t("dben_meet"), t("dben_wait"), t("dben_247")];

  const related = ATTRACTIONS.filter((x) => x.slug !== a.slug && x.region === a.region).slice(0, 3);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="attraction-detail-page">
      <Seo title={title} description={desc} path={`/ausfluege/${a.slug}`}
        jsonLd={[orgSchema(),
          breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_attractions"), path: "/ausfluege" }, { name: c.name, path: `/ausfluege/${a.slug}` }]),
          { "@context": "https://schema.org", "@type": "TouristAttraction", name: c.name, description: desc, address: { "@type": "PostalAddress", addressRegion: a.region, addressCountry: "CH" } },
          faqSchema(faqs)]} />

      <Link to="/ausfluege" className="inline-flex items-center gap-2 font-body text-sm text-[#5A607A] hover:text-[#0B1533] transition-colors duration-200 mb-6"><ArrowLeft size={15} /> {t("attractions_all")}</Link>
      <span className="font-body text-xs font-semibold text-[#EF5A1E] bg-[#EEF1FF] rounded-full px-3 py-1">{a.region}</span>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-tight mt-3">{t("att_h1_prefix")}: {c.name}</h1>
      <p className="font-body text-lg text-[#3A3F55] leading-relaxed mt-6">{c.intro}</p>
      <p className="font-body text-lg text-[#3A3F55] leading-relaxed mt-3">{c.why}</p>

      <div className="mt-10"><TransferCta cityId={a.cityId} dropoffName={c.name} testid="attraction-cta" /></div>

      <div className="grid sm:grid-cols-2 gap-3 mt-10">
        {benefits.map((b, i) => (
          <div key={i} className="flex items-center gap-3 rounded-2xl bg-[#F5F2ED] px-5 py-4">
            <CheckCircle size={20} weight="fill" className="text-[#EF5A1E] shrink-0" />
            <span className="font-body text-sm text-[#0B1533]">{b}</span>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("dfaq_h2")}</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`attraction-faq-${i}`}>
              <p className="font-body font-semibold text-[#0B1533]">{f.q}</p>
              <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("attractions_related")}</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/ausfluege/${r.slug}`} className="group flex items-center justify-between gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#EF5A1E] transition-colors duration-200">
                <span className="flex items-center gap-2.5 font-body text-sm text-[#0B1533]"><MapPin size={16} className="text-[#EF5A1E]" /> {(r[lang] || r.de).name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
